/**
 * The `--widget-*` properties a searchbar sets from its config.
 *
 * One builder for every surface that draws a searchbar's pieces: the widget's
 * own root, and the platform transcript that shows the carousels a visitor
 * was shown — so a program card reads the same in both.
 */

import type React from 'react';
import type { WebchatConfig } from './types';
import { shiftHue, withAlpha } from '../colorUtils';
import { resolveFontStack } from '../fonts';

/** The searchbar's own fallback accent, which it applies when none is set. */
export const SEARCHBAR_DEFAULT_PRIMARY = '#7c3aed';

export function searchbarThemeVars(webchatConfig: WebchatConfig): React.CSSProperties {
  const primary = webchatConfig.primaryColor || SEARCHBAR_DEFAULT_PRIMARY;
  const accent = shiftHue(primary, 40);

  // When a card background is configured, the whole program card renders as
  // that solid color: text/pills/borders derive from the card text color
  // (white by default) and the gradient accent bar is hidden.
  const cardThemeVars: React.CSSProperties = (() => {
    if (!webchatConfig.cardBgColor) return {};
    const cardText = webchatConfig.cardTextColor || '#ffffff';
    return {
      ['--widget-card-bg' as string]: webchatConfig.cardBgColor,
      ['--widget-card-text' as string]: cardText,
      ['--widget-card-border' as string]: withAlpha(cardText, 0.22),
      ['--widget-card-pill-bg' as string]: withAlpha(cardText, 0.16),
      ['--widget-card-muted' as string]: withAlpha(cardText, 0.72),
      ['--widget-card-accent' as string]: 'transparent',
    };
  })();

  // When a form background is configured, the dynamic-form drawer renders on
  // that surface: text and separators derive from the form text color (white
  // by default) while the input controls stay white for legibility.
  const formThemeVars: React.CSSProperties = (() => {
    if (!webchatConfig.formBgColor) return {};
    const formText = webchatConfig.formTextColor || '#ffffff';
    return {
      ['--widget-form-bg' as string]: webchatConfig.formBgColor,
      ['--widget-form-text' as string]: formText,
      ['--widget-form-border' as string]: withAlpha(formText, 0.18),
      ['--widget-form-muted' as string]: withAlpha(formText, 0.7),
    };
  })();

  const fontFamily = resolveFontStack(webchatConfig.fontFamily);
  const secondaryFontFamily = resolveFontStack(webchatConfig.secondaryFontFamily);

  return {
    ['--widget-primary' as string]: primary,
    ['--widget-accent' as string]: accent,
    ...(webchatConfig.carouselImageAspect ? { ['--carousel-image-aspect' as string]: webchatConfig.carouselImageAspect } : {}),
    ...(webchatConfig.userMessageBgColor ? { ['--widget-user-bubble' as string]: webchatConfig.userMessageBgColor } : {}),
    ...(webchatConfig.userMessageTextColor ? { ['--widget-user-text' as string]: webchatConfig.userMessageTextColor } : {}),
    ...(webchatConfig.assistantMessageBgColor ? { ['--widget-assistant-bubble' as string]: webchatConfig.assistantMessageBgColor } : {}),
    ...(webchatConfig.assistantMessageTextColor ? { ['--widget-assistant-text' as string]: webchatConfig.assistantMessageTextColor } : {}),
    ...(fontFamily ? { ['--widget-font-family' as string]: fontFamily } : {}),
    ...(secondaryFontFamily ? { ['--widget-font-family-secondary' as string]: secondaryFontFamily } : {}),
    ...(webchatConfig.regularTextSize ? { ['--widget-text-size-regular' as string]: `${webchatConfig.regularTextSize}px` } : {}),
    ...(webchatConfig.specialTextSize ? { ['--widget-text-size-special' as string]: `${webchatConfig.specialTextSize}px` } : {}),
    ...(webchatConfig.quickActionFontSize ? { ['--widget-text-size-quick-action' as string]: `${webchatConfig.quickActionFontSize}px` } : {}),
    ...(webchatConfig.formTextSize ? { ['--widget-form-text-size' as string]: `${webchatConfig.formTextSize}px` } : {}),
    ...(webchatConfig.formButtonFontSize ? { ['--widget-form-button-size' as string]: `${webchatConfig.formButtonFontSize}px` } : {}),
    ...(webchatConfig.topButtonFontSize ? { ['--widget-top-button-size' as string]: `${webchatConfig.topButtonFontSize}px` } : {}),
    ...(webchatConfig.roundedButtons === false
      ? {
          ['--widget-button-radius' as string]: 'var(--widget-radius)',
          ['--widget-searchbar-radius' as string]: 'var(--widget-radius-sm)',
        }
      : {}),
    ...cardThemeVars,
    ...formThemeVars,
    ...(webchatConfig.cardBadgeBgColor ? { ['--widget-card-badge-bg' as string]: webchatConfig.cardBadgeBgColor } : {}),
    ...(webchatConfig.quickActionBgColor ? { ['--widget-quick-action-bg' as string]: webchatConfig.quickActionBgColor } : {}),
    ...(webchatConfig.quickActionTextColor ? { ['--widget-quick-action-text' as string]: webchatConfig.quickActionTextColor } : {}),
  };
}
